import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Col,
  Icon,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  notification,
} from "antd";
import { Formik } from "formik";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Creators as SupplementActions } from "../../../store/ducks/supplement";
import { Creators as SupplierActions } from "../../../store/ducks/supplier";
import ButtonStandard from "../../utils/button";
import NoteIcon from "../../utils/icons/note";
import PencilIcon from "../../utils/icons/pencil";
import PlusBlueIcon from "../../utils/icons/plus/blue";
import InfoTooltip from "../../utils/infoTooltip";
import DrawerSupplier from "../supplier";
import AlertIcon from "./icons/alert";
import SuccessIcon from "./icons/success";
import {
  Bold,
  Container,
  CustomDivider,
  Footer,
  RadioButtonCustom,
  RadioGroupCustom,
  SupplementInfoCard,
  Table,
} from "./styles";
import DrawerSupplementSustenanceCapacity from "./supplementSustenanceCapacity";

import { getAllSuppliers } from "../../../services/supplierService";
import { isObjectEmptyOrNull } from "../../../utils/objectUtils";
import SearchSupplement from "../../utils/searchSupplement";
import DrawerSupplementRestrictions from "./supplementRestrictions";
import { getSupplementShow } from "../../../services/supplementService";
import { getIndexIntegrationsFarm } from "../../../services/integrationPartnersService";

const INITIAL_FORM_SUPPLEMENT_VALUES = {
  originalSupplementId: null,
  id: null,
  stockItemId: null,
  groupId: null,
  farmId: null,
  name: "",
  importCode: "",
  stabilityRange: "",
  stabilityTime: "",
  supplementFormulated: false,
  type: "Supplement",
  classification: "MineralEnergyProtein",
  industrialName: "",
  supplierId: null,
  cost: "",
  cp: "",
  cpUnit: "g_kg",
  tdn: "",
  tdnUnit: "g_kg",
  dryMatterPercentage: "",
  nitrogen: "",
  phosphorus: "",
  zinc: "",
  sodium: "",
  calcium: "",
  sulfur: "",
  potassium: "",
  potassiumUnit: "percentage",
  nitrogenUnit: "g_kg",
  calciumUnit: "g_kg",
  phosphorusUnit: "g_kg",
  zincUnit: "g_kg",
  sodiumUnit: "g_kg",
  sulfurUnit: "g_kg",
  proportion: null,
  status: "Active",
  additiveEffect: null,
  additiveEffectUnit: "G_DAY",
  fda: "",
  lignin: "",
  ee: "",
  supplementGroupId: null,
  supplementSubGroupId: null,
  supplementPresentationId: null,
  fdn: "", // FDN
  pdr: "", // PDR/%PB
  pndr: "", // PNDR/%PB
  em: "", // EM (Mcal/kg)
  fef: "", // FEF %
  fdnfe: "", // FDNfe %
  farmRestrictions: null,
};

const INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES = {
  showMix: false,
  minimumVoluntaryConsumption: "",
  maximumVoluntaryConsumption: "",
  voluntaryConsumptionType: "G_ANIMAL",
  minimumPure: "",
  minimumPureTrough: "",
  maximumPure: "",
  maximumPureTrough: "",
  pureConsumptionType: "G_ANIMAL",
  minimumMix: "",
  minimumMixTrough: "",
  maximumMix: "",
  maximumMixTrough: "",
  mixConsumptionType: "G_ANIMAL",
  mixRatio: "",
  status: "NI",
};

const INITIAL_FORM_FARM_RESTRICTIONS_VALUES = {
  reproductiveStatusList: [],
  minSupplementOfferedPerDay: null,
  maxSupplementOfferedPerDay: null,
  supplementOfferedUnitType: "PV_ANIMAL",
  maxDays: null,
  minMaleAgeMonths: null,
  maxMaleAgeMonths: null,
  minFemaleAgeMonths: null,
  maxFemaleAgeMonths: null,
  minCalfAgeMonths: null,
  maxCalfAgeMonths: null,
  minHeiferAgeMonths: null,
  maxHeiferAgeMonths: null,
  minMaleLiveWeight: null,
  maxMaleLiveWeight: null,
  minFemaleLiveWeight: null,
  maxFemaleLiveWeight: null,
  minCalfLiveWeight: null,
  maxCalfLiveWeight: null,
  minHeiferLiveWeight: null,
  maxHeiferLiveWeight: null,
  status: "NI",
};

const SUPPLEMENT_GROUPS = [
  { id: 1, name: "Matérias-Primas" },
  { id: 2, name: "Suplementos & Concentrados" },
  { id: 3, name: "Rações" },
];

const SUPPLEMENT_SUB_GROUPS = [
  { id: 1, name: "Forragens Verdes", supplement_group_id: 1 },
  { id: 2, name: "Forragens Secas", supplement_group_id: 1 },
  { id: 3, name: "Resíduos Agrícolas", supplement_group_id: 1 },
  { id: 4, name: "Proteicos", supplement_group_id: 1 },
  { id: 5, name: "Energéticos", supplement_group_id: 1 },
  { id: 6, name: "Minerais", supplement_group_id: 1 },
  { id: 7, name: "S&C Prot/Energ", supplement_group_id: 2 },
  { id: 8, name: "S&C Prot/Energ/Minerais", supplement_group_id: 2 },
  { id: 9, name: "S Mineral", supplement_group_id: 2 },
  { id: 10, name: "S Ração", supplement_group_id: 3 },
];

const SUPPLEMENT_PRESENTATION = [
  { id: 1, name: "Gramínea" },
  { id: 2, name: "Silagem" },
  { id: 3, name: "Feno" },
  { id: 4, name: "Resíduo / Subproduto" },
  { id: 5, name: "Concentrado Seco" },
  { id: 6, name: "Concentrado Úmido" },
];

const NUTRIENT_UNITS = [
  { label: "g/kg", value: "g_kg" },
  { label: "mg/kg", value: "mg_kg" },
  { label: "%", value: "percentage" },
];

const TITLE_SUBMIT_SUPPLEMENT_FORM =
  "Ao confirmar, os itens abaixo devem ser considerados:";
const CONTENT_SUBMIT_SUPPLEMENT_FORM = (
  <ul>
    <li style={{ marginBottom: 26, marginTop: 25 }}>
      Atualize as informações de Suplemento/Concent. em todas as dietas em{" "}
      <Bold>Edição de "Estratégia de dieta", </Bold>
      caso contrário, todas as estratégias estarão considerando os valores
      antigos deste Suplemento/Concent.
    </li>
    <li style={{ marginBottom: 26 }}>
      Após finalizar o passo acima, <Bold>reprocesse</Bold> todas os Cenários de
      venda - <Bold>Favoritos</Bold> que utilizam as
      <Bold> estratégias acima como baseline,</Bold> caso contrário, todos os
      cenários estarão considerando os valores antigos deste Suplemento/Concent.
    </li>
    <li>
      Para os <Bold>Cenários de compra,</Bold> você deve atualizar a estratégia
      de dieta e reprocessar o cenário em{" "}
      <Bold>Edição de Cenário de compra</Bold>, caso contrário, todas as
      estratégias e cenários de compra estarão considerando os valores antigos
      deste Suplemento/Concent.
    </li>
  </ul>
);

function DrawerSupplement() {
  const [isLoadingRequestSaveOrUpdate, setIsLoadingRequestSaveOrUpdate] =
    useState(false);
  const [textSearchSupplement, setTextSearchSupplement] = useState(null);
  const [formSupplement, setFormSupplement] = useState(
    INITIAL_FORM_SUPPLEMENT_VALUES,
  );
  const [sustenanceCapacityData, setSustenanceCapacityData] = useState(
    INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES,
  );
  const [supplementFarmRestrictionsData, setSupplementFarmRestrictionsData] =
    useState(INITIAL_FORM_FARM_RESTRICTIONS_VALUES);
  const [saveOptions, setSaveOptions] = useState("only_save");
  const [
    drawerSupplementSustenanceCapacityVisible,
    setDrawerSupplementSustenanceCapacityVisible,
  ] = useState(false);
  const [
    drawerSupplementFarmRestrictionsVisible,
    setDrawerSupplementFarmRestrictionsVisible,
  ] = useState(false);
  const [onlyReadSupplier, setOnlyReadSupplier] = useState(null);
  const [isAutomacaoTratosEnabled, setIsAutomacaoTratosEnabled] =
    useState(false);

  const formik = useRef();

  const { Option } = Select;

  const {
    app: {
      translation,
      groupSelected,
      groupSelected: { id: groupId },
      farmSelected,
      farmSelected: { id: farmId },
      user: { isInputSupplier },
    },
    supplement: {
      supplementData,
      idSupplement,
      sustenanceCapacityData: savedSustenanceCapacity,
      farmRestrictionsData: savedFarmRestrictions,
      drawerVisible,
      defaultSupplementGroupId,
    },
    supplier: {
      listDrowpDown: suppliers,
      isLoadingDropDown: isLoadingDropDownSuppliers,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    supplementGroupId: Yup.number().required(),
    supplementSubGroupId: Yup.number().required(),
    supplementPresentationId: Yup.number().required(),
    supplierId: !isInputSupplier ? Yup.string().required() : null,
    cost: Yup.number().when("stockItemId", {
      is: (stockItemId) => stockItemId != null && stockItemId !== "",
      then: Yup.number().nullable().notRequired(),
      otherwise: Yup.number().required(),
    }),
    tdn: Yup.number().required(),
    cp: Yup.number().required(),
    dryMatterPercentage: Yup.number().min(0).max(100).required(),
  });

  const blockEdit = useMemo(() => {
    return (
      supplementData &&
      (supplementData.referenceSupplementId != null ||
        supplementData.baseSupplement)
    );
  }, [supplementData]);

  const blockSupplier = useMemo(() => {
    let block = null;
    if (supplementData && supplementData.supplierId && suppliers?.length > 0) {
      block =
        suppliers.find((supplier) => {
          if (supplier.id === supplementData.supplierId) {
            return true;
          }
          return false;
        }) == null
          ? true
          : false;
    }
    return block;
  }, [supplementData, suppliers]);

  /** Methods */

  const selectNutrientsUnits = useCallback((field, value, setFieldValue) => {
    return (
      <Select
        value={value || undefined}
        onChange={(valueSelected) => setFieldValue(field, valueSelected)}
      >
        {NUTRIENT_UNITS.map((unit) => (
          <Select.Option key={unit.value} value={unit.value}>
            {unit.label}
          </Select.Option>
        ))}
      </Select>
    );
  }, []);

  const selectAdditiveEffectUnits = (props, blockEdit) => {
    return (
      <Select
        disabled={blockEdit}
        value={props.values.additiveEffectUnit}
        style={{ width: 90 }}
        onChange={(value) => props.setFieldValue("additiveEffectUnit", value)}
      >
        <Select.Option value="G_DAY">g/dia</Select.Option>
        <Select.Option value="PERCENTAGE_PB">% PB</Select.Option>
      </Select>
    );
  };

  async function fetchDataSupplement() {
    setIsLoadingRequestSaveOrUpdate(true);
    try {
      const {
        data: { results },
      } = await getSupplementShow({
        groupId,
        farmId,
        id: idSupplement,
        isInputSupplier: false,
      });
      setFormSupplement(results);
      setSustenanceCapacityData({
        showMix: results.showMix,
        minimumVoluntaryConsumption: results.minimumVoluntaryConsumption,
        maximumVoluntaryConsumption: results.maximumVoluntaryConsumption,
        voluntaryConsumptionType: results.voluntaryConsumptionType,
        minimumPure: results.minimumPure,
        minimumPureTrough: results.minimumPureTrough,
        maximumPure: results.maximumPure,
        maximumPureTrough: results.maximumPureTrough,
        pureConsumptionType: results.pureConsumptionType,
        minimumMix: results.minimumMix,
        minimumMixTrough: results.minimumMixTrough,
        maximumMix: results.maximumMix,
        maximumMixTrough: results.maximumMixTrough,
        mixConsumptionType: results.mixConsumptionType,
        mixRatio: results.mixRatio,
        status: "I",
      });
      setSupplementFarmRestrictionsData(
        results.restriction ?? INITIAL_FORM_FARM_RESTRICTIONS_VALUES,
      );
    } catch (error) {
    } finally {
      setIsLoadingRequestSaveOrUpdate(false);
    }
  }

  const getSuppliers = async () => {
    dispatch(
      SupplierActions.getDropdownSuppliers(groupId, farmId, "Supplement", true),
    );
  };

  const closeDrawer = () => {
    dispatch(SupplementActions.hideDrawer());
  };

  const openNotificationError = (title, message) => {
    notification["error"]({
      message: title,
      description: message,
    });
  };

  const handleClearNutritionalData = useCallback((setFieldValue) => {
    setFieldValue("cp", "");
    setFieldValue("tdn", "");
    setFieldValue("dryMatterPercentage", "");
    setFieldValue("nitrogen", "");
    setFieldValue("phosphorus", "");
    setFieldValue("zinc", "");
    setFieldValue("sodium", "");
    setFieldValue("calcium", "");
    setFieldValue("sulfur", "");
    setFieldValue("proportion", "");
    setFieldValue("additiveEffect", "");
    setFieldValue("additiveEffectUnit", "");
    setFieldValue("fda", "");
    setFieldValue("lignin", "");
    setFieldValue("ee", "");
    setFieldValue("supplementGroupId", null);
    setFieldValue("supplementSubGroupId", null);
    setFieldValue("supplementPresentationId", null);
    setFieldValue("fdn", "");
    setFieldValue("pdr", "");
    setFieldValue("pndr", "");
    setFieldValue("em", "");
    setFieldValue("fef", "");
    setFieldValue("fdnfe", "");
  }, []);

  const handleDeselectSupplement = useCallback(
    (setFieldValue) => {
      Modal.confirm({
        centered: true,
        content: translation.supplement.confirmClearNutritionalData,
        cancelText: translation.buttons.confirm,
        okText: translation.buttons.no,
        onCancel: () => {
          setFieldValue("originalSupplementId", null);
          handleClearNutritionalData(setFieldValue);
        },
        onOk: () => {
          setFieldValue("originalSupplementId", null);
        },
      });
    },
    [
      handleClearNutritionalData,
      translation.buttons.confirm,
      translation.buttons.no,
      translation.supplement.confirmClearNutritionalData,
    ],
  );

  const handleSelectSupplement = useCallback((supplement, setFieldValue) => {
    const lastVersion = supplement.versions[supplement.versions.length - 1];

    setFieldValue("cp", lastVersion.cp);
    setFieldValue("cpUnit", lastVersion.cpUnit);
    setFieldValue("tdn", lastVersion.tdn);
    setFieldValue("tdnUnit", lastVersion.tdnUnit);
    setFieldValue("dryMatterPercentage", lastVersion.dryMatterPercentage);
    setFieldValue("nitrogen", lastVersion.nitrogen);
    setFieldValue("phosphorus", lastVersion.phosphorus);
    setFieldValue("zinc", lastVersion.zinc);
    setFieldValue("sodium", lastVersion.sodium);
    setFieldValue("calcium", lastVersion.calcium);
    setFieldValue("sulfur", lastVersion.sulfur);
    setFieldValue("potassium", lastVersion.potassium);
    setFieldValue("potassiumUnit", lastVersion.potassiumUnit);
    setFieldValue("nitrogenUnit", lastVersion.nitrogenUnit);
    setFieldValue("calciumUnit", lastVersion.calciumUnit);
    setFieldValue("phosphorusUnit", lastVersion.phosphorusUnit);
    setFieldValue("zincUnit", lastVersion.zincUnit);
    setFieldValue("sodiumUnit", lastVersion.sodiumUnit);
    setFieldValue("sulfurUnit", lastVersion.sulfurUnit);
    setFieldValue("proportion", lastVersion.proportion);
    setFieldValue("additiveEffect", lastVersion.additiveEffect);
    setFieldValue("additiveEffectUnit", lastVersion.additiveEffectUnit);
    setFieldValue("fda", lastVersion.fda);
    setFieldValue("lignin", lastVersion.lignin);
    setFieldValue("ee", lastVersion.ee);
    setFieldValue("supplementGroupId", supplement.supplementGroupId);
    setFieldValue("supplementSubGroupId", supplement.supplementSubGroupId);
    setFieldValue(
      "supplementPresentationId",
      supplement.supplementPresentationId,
    );
    setFieldValue("fdn", lastVersion.fdn);
    setFieldValue("pdr", lastVersion.pdr);
    setFieldValue("pndr", lastVersion.pndr);
    setFieldValue("em", lastVersion.em);
    setFieldValue("fef", lastVersion.fef);
    setFieldValue("fdnfe", lastVersion.fdnfe);
  }, []);

  const handleNewSupplement = (supplement) => {
    const isOnlySave = saveOptions === "only_save" ? false : true;
    const body = {
      ...supplement,
      status: "Active",
    };
    dispatch(
      SupplementActions.saveSupplement(
        groupId,
        farmId,
        null,
        body,
        isOnlySave,
        isInputSupplier,
      ),
    );
  };

  const handleUpdateSupplement = (supplement, id) => {
    const isOnlySave = saveOptions === "only_save" ? false : true;
    dispatch(
      SupplementActions.saveSupplement(
        groupId,
        farmId,
        id,
        supplement,
        isOnlySave,
        isInputSupplier,
      ),
    );
  };

  const handleSubmitModalForm = async (values, actions) => {
    if (values.groupId == null && values.farmId == null) {
      values.groupId = groupId;
      values.farmId = farmId;
    }
    if (values.baseSupplement && !isInputSupplier) {
      values.referenceSupplementId = values.id;
      values.id = null;
    }

    let sustenanceCapacityToBeSaveData =
      sustenanceCapacityData.status === "I"
        ? sustenanceCapacityData
        : savedSustenanceCapacity;

    if (
      sustenanceCapacityToBeSaveData &&
      sustenanceCapacityToBeSaveData.status === "NI"
    ) {
      openNotificationError(
        "Dados incompletos",
        translation.supplement.errorNecessarySustenanceCapacity,
      );
    } else if (
      sustenanceCapacityToBeSaveData &&
      sustenanceCapacityToBeSaveData.status === "I"
    ) {
      const body = {
        ...values,
        ...sustenanceCapacityData,
        status: values.status,
        restriction:
          supplementFarmRestrictionsData &&
          supplementFarmRestrictionsData.status === "I"
            ? {
                ...supplementFarmRestrictionsData,
              }
            : null,
        groupId: values.groupId,
        farmId: values.farmId,
      };
      if (
        isAutomacaoTratosEnabled &&
        body.supplementGroupId === 1 &&
        (body.stabilityTime == null || body.stabilityTime === "")
      ) {
        body.stabilityTime = 3;
      }
      // Check if the voluntary consumption type is valid, and is equal to the restriction type
      if (
        body.restriction &&
        body.restriction.minSupplementOfferedPerDay &&
        body.voluntaryConsumptionType !==
          body.restriction.supplementOfferedUnitType
      ) {
        openNotificationError(
          "Dados incompletos",
          translation.supplement.errorVoluntaryConsumptionType,
        );
        return;
      }
      if (body.id === null) {
        if (!isInputSupplier) {
          Modal.confirm({
            title: TITLE_SUBMIT_SUPPLEMENT_FORM,
            content: CONTENT_SUBMIT_SUPPLEMENT_FORM,
            width: 580,
            icon: <Icon type="reload" />,
            centered: true,
            onOk: () => handleNewSupplement(body),
          });
        } else {
          handleNewSupplement(body);
        }
      } else {
        if (!isInputSupplier) {
          Modal.confirm({
            title: TITLE_SUBMIT_SUPPLEMENT_FORM,
            content: CONTENT_SUBMIT_SUPPLEMENT_FORM,
            width: 580,
            icon: <Icon type="reload" />,
            centered: true,
            onOk: () => handleUpdateSupplement(body, body.id),
          });
        } else {
          handleUpdateSupplement(body, body.id);
        }
      }
    } else if (sustenanceCapacityToBeSaveData == null) {
      openNotificationError(
        "Dados incompletos",
        translation.supplement.errorNecessarySustenanceCapacity,
      );
    }
  };

  const handleDrawerVisible = async (visible) => {
    if (visible) {
      setTextSearchSupplement(null);
      formik.current.resetForm({
        ...INITIAL_FORM_SUPPLEMENT_VALUES,
        groupId,
        farmId,
        formulateItems: [],
        supplementGroupId: defaultSupplementGroupId ?? null,
      });
      getSuppliers();
      if (idSupplement) {
        fetchDataSupplement();
      } else {
        setFormSupplement({
          ...INITIAL_FORM_SUPPLEMENT_VALUES,
          groupId,
          farmId,
          formulateItems: [],
          supplementGroupId: defaultSupplementGroupId ?? null,
        });
        setSustenanceCapacityData(INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES);
        setSupplementFarmRestrictionsData(
          INITIAL_FORM_FARM_RESTRICTIONS_VALUES,
        );
      }
    } else {
      formik.current.resetForm({
        ...INITIAL_FORM_SUPPLEMENT_VALUES,
        groupId,
        farmId,
      });
      setTextSearchSupplement(null);
      setFormSupplement({ ...INITIAL_FORM_SUPPLEMENT_VALUES, groupId, farmId });
      setSustenanceCapacityData(INITIAL_FORM_SUSTENANCE_CAPACITY_VALUES);
      setSupplementFarmRestrictionsData(INITIAL_FORM_FARM_RESTRICTIONS_VALUES);
    }
  };

  const handleSaveSustenanceCapacity = (data) => {
    if (data != null) {
      dispatch(SupplementActions.updateSupplementSustenanceCapacityData(data));
      setSustenanceCapacityData(data);
      setDrawerSupplementSustenanceCapacityVisible(false);
    }
  };

  const handleCancelSustenanceCapacity = () => {
    setDrawerSupplementSustenanceCapacityVisible(false);
  };

  const handleSaveFarmRestrictionsData = (data) => {
    if (data != null) {
      const isFarmRestrictionNotInformed = isObjectEmptyOrNull({
        ...data,
        farmId: null,
        groupId: null,
        supplementOfferedUnitType: null,
        status: null,
      });
      setSupplementFarmRestrictionsData({
        ...data,
        status: isFarmRestrictionNotInformed ? "NI" : "I",
      });
      setDrawerSupplementFarmRestrictionsVisible(false);
    }
  };

  const handleCancelFarmRestrictionsData = () => {
    setDrawerSupplementFarmRestrictionsVisible(false);
  };

  const handleCreateSupplier = useCallback(() => {
    dispatch(SupplierActions.showDrawer("new_supplement_supplier"));
  }, [dispatch]);

  /* Method will be use in button on SustenanceCapacity */
  const handleEditSustenanceCapacity = (
    supplement,
    sustenanceCapacityData,
    blockEdit,
  ) => {
    setSustenanceCapacityData((oldState) => ({
      ...oldState,
      showMix: supplement.type !== "Supplement",
      blockEdit,
    }));
    setDrawerSupplementSustenanceCapacityVisible(true);
  };

  const handleEditFarmRestrictions = () => {
    setDrawerSupplementFarmRestrictionsVisible((oldState) => !oldState);
  };

  const handleLoadSupplier = useCallback(
    async (groupId, farmId, supplierId) => {
      const {
        data: { results },
      } = await getAllSuppliers({
        groupId: groupId,
        farmId,
        withoutPagination: true,
        type: "Supplement",
        id: supplierId,
      });
      setOnlyReadSupplier(results.name);
    },
    [],
  );

  useEffect(() => {
    if (blockSupplier && onlyReadSupplier == null && supplementData) {
      handleLoadSupplier(groupSelected.id, null, supplementData.supplierId);
    }
  }, [
    blockSupplier,
    groupSelected.id,
    handleLoadSupplier,
    onlyReadSupplier,
    supplementData,
  ]);

  useEffect(() => {
    async function loadIntegrations() {
      try {
        const {
          data: { results },
        } = await getIndexIntegrationsFarm({ groupId, farmId });
        setIsAutomacaoTratosEnabled(
          (results || []).some(
            (i) =>
              i.status === "Active" &&
              i.integrationPartner?.partnerSystemName === "StarBras",
          ),
        );
      } catch (error) {
        setIsAutomacaoTratosEnabled(false);
      }
    }
    if (drawerVisible && groupId && farmId) {
      loadIntegrations();
    }
  }, [drawerVisible, groupId, farmId]);

  useEffect(() => {
    let update = true;
    if (update && savedSustenanceCapacity) {
      setSustenanceCapacityData(savedSustenanceCapacity);
    }

    return () => {
      update = false;
    };
  }, [savedSustenanceCapacity]);

  useEffect(() => {
    let update = true;
    if (update && savedFarmRestrictions) {
      setSupplementFarmRestrictionsData(savedFarmRestrictions);
    }

    return () => {
      update = false;
    };
  }, [savedFarmRestrictions]);

  return (
    <Container
      title={
        idSupplement != null
          ? translation.supplement.drawerTitleEdit
          : translation.supplement.drawerTitle
      }
      width={650}
      onClose={closeDrawer}
      maskClosable={false}
      visible={drawerVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Formik
        ref={formik}
        enableReinitialize={true}
        initialValues={formSupplement}
        onSubmit={handleSubmitModalForm}
        validationSchema={validationSchema}
        render={(props) => (
          <form onSubmit={props.handleSubmit} autoComplete="off">
            <Spin spinning={isLoadingRequestSaveOrUpdate}>
              <div className="drawerForm">
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <span style={{ fontSize: "14px", color: "#000" }}>
                      {idSupplement != null
                        ? translation.supplement.drawerSubTitleEdit
                        : translation.supplement.drawerSubTitle}
                    </span>
                  </Col>
                </Row>
                {/* Supplement Visibility */}
                {!isInputSupplier && !idSupplement && (
                  <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label>
                          {translation.supplement.form.groupFarmVisibility}
                        </label>
                      </Row>
                      <Row>
                        <RadioGroupCustom
                          value={
                            props.values.groupId != null &&
                            props.values.farmId != null
                              ? "farm"
                              : props.values.groupId != null &&
                                  props.values.farmId == null
                                ? "group"
                                : "farm"
                          }
                          buttonStyle="solid"
                        >
                          <RadioButtonCustom
                            value="farm"
                            onChange={(e) => {
                              props.setFieldValue("groupId", groupSelected.id);
                              props.setFieldValue("farmId", farmSelected.id);
                            }}
                          >
                            {translation.supplier.form.radiogroup.farm}
                          </RadioButtonCustom>
                          <RadioButtonCustom
                            value="group"
                            onChange={(e) => {
                              props.setFieldValue("groupId", groupSelected.id);
                              props.setFieldValue("farmId", null);
                            }}
                          >
                            {translation.supplement.form.radiogroup.group}:
                            {groupSelected.name}
                          </RadioButtonCustom>
                        </RadioGroupCustom>
                      </Row>
                    </Col>
                  </Row>
                )}
                {/* Errors */}
                {Object.entries(props.errors).length > 0 &&
                  props.submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                {/* Mat Original */}
                {!idSupplement && (
                  <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label>
                          {translation.supplement.form.originalSupplement}
                        </label>
                      </Row>
                      <Row>
                        <SearchSupplement
                          textSearch={textSearchSupplement}
                          setTextSearch={setTextSearchSupplement}
                          value={props.values.originalSupplementId || undefined}
                          handleChange={(value, supplementSelected) => {
                            props.setFieldValue("originalSupplementId", value);
                            if (value) {
                              handleSelectSupplement(
                                supplementSelected,
                                props.setFieldValue,
                              );
                            }
                          }}
                          showDeselect
                          handleDeselect={(supplement) =>
                            handleDeselectSupplement(props.setFieldValue)
                          }
                        />
                      </Row>
                    </Col>
                  </Row>
                )}
                {/* Supplement Group */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementGroupId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementGroupId}*
                        <InfoTooltip
                          title={translation.supplement.form.supplementTypeHelp}
                        />
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={props.values.supplementGroupId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) => {
                          props.setFieldValue("supplementGroupId", value);
                          props.setFieldValue("supplementSubGroupId", null);
                        }}
                      >
                        {SUPPLEMENT_GROUPS.map((group) => (
                          <Select.Option key={group.id} value={group.id}>
                            {group.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
                {/* Supplement Sub Group */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementSubGroupId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementSubGroupId}*
                        {/* <InfoTooltip
                            title={
                              <>
                                <ol style={{ marginLeft: 16 }}>
                                  <li>
                                    <strong>
                                      {
                                        translation.supplement.form
                                          .supplementClassificationHelp.title_0
                                      }
                                      :
                                    </strong>
                                    {
                                      translation.supplement.form
                                        .supplementClassificationHelp
                                        .description_0
                                    }
                                  </li>
                                  <li>
                                    <strong>
                                      {
                                        translation.supplement.form
                                          .supplementClassificationHelp.title_1
                                      }
                                      :
                                    </strong>
                                    {
                                      translation.supplement.form
                                        .supplementClassificationHelp
                                        .description_1
                                    }
                                  </li>
                                  <li>
                                    <strong>
                                      {
                                        translation.supplement.form
                                          .supplementClassificationHelp.title_2
                                      }
                                      :
                                    </strong>
                                    {
                                      translation.supplement.form
                                        .supplementClassificationHelp
                                        .description_2
                                    }
                                  </li>
                                </ol>
                                <p>
                                  {
                                    translation.supplement.form
                                      .supplementClassificationHelp.source
                                  }
                                </p>
                              </>
                            }
                          /> */}
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={props.values.supplementSubGroupId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          props.setFieldValue("supplementSubGroupId", value)
                        }
                      >
                        {SUPPLEMENT_SUB_GROUPS.filter(
                          (subgroup) =>
                            props.values.supplementGroupId &&
                            subgroup.supplement_group_id ===
                              props.values.supplementGroupId,
                        ).map((subgroup) => (
                          <Select.Option key={subgroup.id} value={subgroup.id}>
                            {subgroup.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
                {/* Supplement Prensetation */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.supplementPresentationId &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.supplementPresentationId}*
                      </label>
                    </Row>
                    <Row>
                      <Select
                        value={
                          props.values.supplementPresentationId || undefined
                        }
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          props.setFieldValue("supplementPresentationId", value)
                        }
                      >
                        {SUPPLEMENT_PRESENTATION.map((presentation) => (
                          <Select.Option
                            key={presentation.id}
                            value={presentation.id}
                          >
                            {presentation.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
                {/* Input supplement identification */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.name && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.name}*
                      </label>
                    </Row>
                    <Row>
                      <Input
                        disabled={blockEdit}
                        placeholder={
                          translation.supplement.form.namePlaceHolder
                        }
                        value={props.values.name}
                        name="name"
                        autoComplete="chrome-off"
                        onChange={(e) => {
                          props.setFieldValue("name", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Import code */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>{translation.supplement.form.importCode}</label>
                    </Row>
                    <Row>
                      <Input
                        disabled={blockEdit}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.importCode}
                        name="importCode"
                        autoComplete="chrome-off"
                        onChange={(e) => {
                          props.setFieldValue("importCode", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {isAutomacaoTratosEnabled &&
                  props.values.supplementGroupId === 1 && (
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      align="middle"
                    >
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Row>
                          <label>
                            {translation.supplement.form.stabilityRange}
                            <InfoTooltip
                              title={
                                translation.supplement.form.stabilityRangeHelp
                              }
                            />
                          </label>
                        </Row>
                        <Row>
                          <NumberFormat
                            customInput={Input}
                            disabled={blockEdit}
                            placeholder={translation.defaultPlaceholder}
                            value={props.values.stabilityRange}
                            name="stabilityRange"
                            decimalScale={0}
                            allowNegative={false}
                            addonAfter="kg"
                            onValueChange={({ floatValue }) => {
                              props.setFieldValue(
                                "stabilityRange",
                                floatValue == null ? "" : floatValue,
                              );
                            }}
                          />
                        </Row>
                      </Col>
                    </Row>
                  )}
                {isAutomacaoTratosEnabled &&
                  props.values.supplementGroupId === 1 && (
                    <Row
                      type="flex"
                      style={{ marginTop: "13px" }}
                      align="middle"
                    >
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Row>
                          <label>
                            {translation.supplement.form.stabilityTime}
                            <InfoTooltip
                              title={
                                translation.supplement.form.stabilityTimeHelp
                              }
                            />
                          </label>
                        </Row>
                        <Row>
                          <NumberFormat
                            customInput={Input}
                            disabled={blockEdit}
                            placeholder={translation.defaultPlaceholder}
                            value={props.values.stabilityTime}
                            name="stabilityTime"
                            decimalScale={0}
                            allowNegative={false}
                            addonAfter="min"
                            onValueChange={({ floatValue }) => {
                              props.setFieldValue(
                                "stabilityTime",
                                floatValue == null ? "" : floatValue,
                              );
                            }}
                          />
                        </Row>
                      </Col>
                    </Row>
                  )}
                {/* Input additional data*/}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>
                        {translation.supplement.form.industrialName}
                      </label>
                    </Row>
                    <Row>
                      <Input
                        disabled={blockEdit}
                        placeholder={
                          translation.supplement.form.industrialNamePlaceHolder
                        }
                        value={props.values.industrialName}
                        name="industrialName"
                        onChange={(e) => {
                          props.setFieldValue("industrialName", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Supplier */}
                {!isInputSupplier && (
                  <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label
                          className={
                            props.errors.supplierId && props.submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.supplement.form.supplierName}*
                        </label>
                      </Row>
                      <Row type="flex" justify="start" align="middle">
                        {blockSupplier === true ? (
                          <Input
                            disabled={true}
                            value={onlyReadSupplier}
                            name="readOnlySupplier"
                            style={{ width: "80%" }}
                          />
                        ) : (
                          <Select
                            disabled={blockEdit || blockSupplier}
                            name="supplierId"
                            value={
                              suppliers != null &&
                              props.values.supplierId != null &&
                              isLoadingDropDownSuppliers === false
                                ? props.values.supplierId
                                : undefined
                            }
                            loading={isLoadingDropDownSuppliers}
                            style={{ width: "80%" }}
                            placeholder={
                              translation.supplement.form
                                .supplierNamePlaceHolder
                            }
                            onChange={(value) => {
                              props.setFieldValue("supplierId", value);
                            }}
                          >
                            {suppliers != null &&
                              suppliers.map((supplier) => (
                                <Option key={supplier.id} value={supplier.id}>
                                  {supplier.name}
                                </Option>
                              ))}
                          </Select>
                        )}

                        <Button
                          disabled={blockEdit || blockSupplier}
                          shape="circle"
                          icon="plus"
                          style={{ marginLeft: 10 }}
                          onClick={handleCreateSupplier}
                        />
                      </Row>
                    </Col>
                  </Row>
                )}
                {/* Cost and Proportion */}
                <Row type="flex" style={{ marginTop: "13px" }} justify="start">
                  {/* Cost */}
                  <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Row>
                      <label
                        className={
                          props.errors.cost && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        Custo{" "}
                        {props.values.stockItemId !== null ? (
                          <InfoTooltip title="Este suplemento está associado a um item de estoque, seu custo é determinado pela as entradas de estoque." />
                        ) : (
                          "*"
                        )}
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.costPlaceHolder
                        }
                        value={props.values.cost}
                        disabled={props.values.stockItemId != null}
                        name="cost"
                        addonBefore="R$"
                        addonAfter="kg"
                        decimalScale={5}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "cost",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 10000
                                  ? 10000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  {/* Proportion */}
                  {props.values.type === "Supplement" && (
                    <Col xs={11} sm={11} md={11} lg={11} xl={11} offset={1}>
                      <Row>
                        <label>Proporção</label>
                      </Row>
                      <Row className="rowInput">
                        <NumberFormat
                          customInput={Input}
                          value={props.values.proportion}
                          placeholder={translation.defaultPlaceholder}
                          addonAfter="%"
                          name="proportion"
                          decimalScale={2}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          onValueChange={({ floatValue }) => {
                            props.setFieldValue(
                              "proportion",
                              floatValue == null
                                ? null
                                : floatValue >= 0
                                  ? floatValue > 100
                                    ? 100
                                    : floatValue
                                  : 0,
                            );
                          }}
                        />
                      </Row>
                    </Col>
                  )}
                </Row>
                <CustomDivider dashed>
                  {translation.supplement.form.nutritionalDataDivider}
                </CustomDivider>
                {/* TDN AND CP */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  {/* TDN */}
                  <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Row>
                      <label
                        className={
                          props.errors.tdn && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.tdn}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={
                          blockEdit || props.values.classification === "Mineral"
                        }
                        customInput={Input}
                        placeholder={translation.supplement.form.tdnPlaceHolder}
                        value={props.values.tdn}
                        name="tdn"
                        addonAfter={selectNutrientsUnits(
                          "tdnUnit",
                          props.values.tdnUnit ?? "g_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "tdn",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  {/* CP */}
                  <Col xs={11} sm={11} md={11} lg={11} xl={11} offset={1}>
                    <Row>
                      <label
                        className={
                          props.errors.cp && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.cp}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.supplement.form.cpPlaceHolder}
                        value={props.values.cp}
                        name="cp"
                        addonAfter={selectNutrientsUnits(
                          "cpUnit",
                          props.values.cpUnit ?? "g_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "cp",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* NPN & DRY MATTER PERCENTAGE */}
                <Row type="flex" style={{ marginTop: "13px" }} align="middle">
                  {/* NPN */}
                  {props.values.classification === "Mineral" && (
                    <Col xs={11} sm={11} md={11} lg={11} xl={11}>
                      <Row>
                        <label
                          className={
                            props.errors.npn && props.submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {translation.supplement.form.npn}
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <NumberFormat
                          customInput={Input}
                          placeholder={
                            translation.supplement.form.npnPlaceHolder
                          }
                          value={props.values.npn}
                          name="npn"
                          addonAfter="g/kg"
                          decimalScale={2}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale={true}
                          onValueChange={({ floatValue }) => {
                            props.setFieldValue(
                              "npn",
                              floatValue == null
                                ? null
                                : floatValue >= 0
                                  ? floatValue > 1000
                                    ? 1000
                                    : floatValue
                                  : 0,
                            );
                          }}
                        />
                      </Row>
                    </Col>
                  )}
                  {/* DRY MATTER PERCENTAGE */}
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    offset={props.values.classification === "Mineral" ? 1 : 0}
                  >
                    <Row>
                      <label
                        className={
                          props.errors.dryMatterPercentage &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.supplement.form.dryMatterPercentage}*
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.dryMatterPercentage}
                        name="dryMatterPercentage"
                        min={0}
                        max={100}
                        addonAfter="%MS"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "dryMatterPercentage",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* CALCIUM, PHOSPHORUS AND NITROGEN */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="middle"
                >
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.calcium}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.calciumPlaceHolder
                        }
                        value={props.values.calcium}
                        name="calcium"
                        addonAfter={selectNutrientsUnits(
                          "calciumUnit",
                          props.values.calciumUnit ?? "g_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "calcium",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.phosphorus}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.phosphorusPlaceHolder
                        }
                        value={props.values.phosphorus}
                        name="phosphorus"
                        addonAfter={selectNutrientsUnits(
                          "phosphorusUnit",
                          props.values.phosphorusUnit ?? "g_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "phosphorus",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.nitrogen}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.nitrogenPlaceHolder
                        }
                        value={props.values.nitrogen}
                        name="nitrogen"
                        addonAfter={selectNutrientsUnits(
                          "nitrogenUnit",
                          props.values.nitrogenUnit ?? "mg_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "nitrogen",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 10000
                                  ? 10000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* ZINC, SODIUM, POTASSIUM, SULFUR  */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="middle"
                >
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.zinc}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.zincPlaceHolder
                        }
                        value={props.values.zinc}
                        name="zinc"
                        addonAfter={selectNutrientsUnits(
                          "zincUnit",
                          props.values.zincUnit ?? "mg_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "zinc",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 10000
                                  ? 10000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.sodium}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.sodiumPlaceHolder
                        }
                        value={props.values.sodium}
                        name="sodium"
                        addonAfter={selectNutrientsUnits(
                          "sodiumUnit",
                          props.values.sodiumUnit ?? "mg_kg",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "sodium",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.potassium}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.potassium}
                        name="potassium"
                        addonAfter={selectNutrientsUnits(
                          "potassiumUnit",
                          props.values.potassiumUnit ?? "percentage",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "potassium",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.sulfur}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.sulfur}
                        name="sulfur"
                        addonAfter={selectNutrientsUnits(
                          "sulfurUnit",
                          props.values.sulfurUnit ?? "percentage",
                          props.setFieldValue,
                        )}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "sulfur",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>
                        {translation.supplement.form.additiveEffect}
                        <InfoTooltip
                          title={translation.supplement.form.additiveEffectHelp}
                        />
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.additiveEffectPlaceHolder
                        }
                        value={props.values.additiveEffect}
                        name="additiveEffect"
                        addonAfter={selectAdditiveEffectUnits(props, blockEdit)}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        maxLength={6}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "additiveEffect",
                            props.values.additiveEffectUnit ===
                              "PERCENTAGE_PB" && floatValue > 99
                              ? 100
                              : floatValue,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* ADDITIVE EFFECT, FDA, LIGNIN, EE */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="middle"
                >
                  <Col xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Row>
                      <label>{translation.supplement.form.fda}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.supplement.form.fdaPlaceHolder}
                        value={props.values.fda}
                        name="fda"
                        addonAfter="g/kg"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        maxLength={6}
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "fda",
                            floatValue != null ? floatValue : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Row>
                      <label>{translation.supplement.form.lignin}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={
                          translation.supplement.form.ligninPlaceHolder
                        }
                        value={props.values.lignin}
                        name="lignin"
                        addonAfter="g/kg"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        maxLength={6}
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "lignin",
                            floatValue != null ? floatValue : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Row>
                      <label>{translation.supplement.form.ee}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.supplement.form.eePlaceHolder}
                        value={props.values.ee}
                        name="ee"
                        addonAfter="%MS"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "ee",
                            floatValue != null
                              ? floatValue > 100
                                ? 100
                                : floatValue
                              : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* FDN, PDR, PNDR */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="middle"
                >
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.fdn}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.fdn}
                        name="fdn"
                        addonAfter={"%"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "fdn",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.pdr}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.pdr}
                        name="pdr"
                        addonAfter={"%PB"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "pdr",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.pndr}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.pndr}
                        name="pndr"
                        addonAfter={"%PB"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "pndr",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* EM, FEF, FDNfe */}
                <Row
                  type="flex"
                  style={{ marginTop: "13px" }}
                  gutter={8}
                  align="middle"
                >
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.em}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.em}
                        name="em"
                        addonAfter={"Mcal/kg"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "em",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.fef}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.fef}
                        name="fef"
                        addonAfter={"%"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "fef",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col xs={8} sm={8} md={8} lg={8} xl={8}>
                    <Row>
                      <label>{translation.supplement.form.fdnfe}</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        disabled={blockEdit}
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={props.values.fdnfe}
                        name="fdnfe"
                        addonAfter={"%"}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "fdnfe",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                                ? floatValue > 1000
                                  ? 1000
                                  : floatValue
                                : 0,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Supplement Capacity */}
                <CustomDivider dashed>
                  {translation.supplement.form.capacityDivider}
                </CustomDivider>
                <Row style={{ marginTop: "13px" }}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Table>
                      <tbody>
                        <tr>
                          <td>
                            <span>
                              {translation.supplement.form.sustenanceCapacity}*
                            </span>
                          </td>
                          <td>
                            {sustenanceCapacityData.status === "NI" ? (
                              <span>
                                <AlertIcon /> {translation.status.pendingData}
                              </span>
                            ) : (
                              <span className="success">
                                <SuccessIcon />{" "}
                                {translation.status.informedData}
                              </span>
                            )}
                          </td>
                          <td>
                            {sustenanceCapacityData.status === "NI" && (
                              <button
                                className="add"
                                type="button"
                                onClick={() =>
                                  handleEditSustenanceCapacity(
                                    props.values,
                                    sustenanceCapacityData,
                                    blockEdit,
                                  )
                                }
                              >
                                {translation.buttons.addInfo}
                                <PlusBlueIcon />
                              </button>
                            )}
                            {sustenanceCapacityData.status === "I" && (
                              <button
                                className="edit"
                                type="button"
                                style={{ marginLeft: "60px" }}
                                onClick={() =>
                                  handleEditSustenanceCapacity(
                                    props.values,
                                    sustenanceCapacityData,
                                    blockEdit,
                                  )
                                }
                              >
                                {translation.buttons.edit}
                                <PencilIcon />
                              </button>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
                {/* Farm Restrictions */}
                <CustomDivider dashed>
                  {translation.supplement.form.restrictionsDivider}
                </CustomDivider>
                <Row style={{ marginTop: "13px" }}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Table>
                      <tbody>
                        <tr>
                          <td>
                            <span>
                              {translation.supplement.form.restrictions}
                            </span>
                          </td>
                          <td>
                            {supplementFarmRestrictionsData.status === "NI" ? (
                              <span>
                                <AlertIcon /> {translation.status.pendingData}
                              </span>
                            ) : (
                              <span className="success">
                                <SuccessIcon />{" "}
                                {translation.status.informedData}
                              </span>
                            )}
                          </td>
                          <td>
                            {supplementFarmRestrictionsData.status === "NI" && (
                              <button
                                className="add"
                                type="button"
                                onClick={handleEditFarmRestrictions}
                              >
                                {translation.buttons.addInfo}
                                <PlusBlueIcon />
                              </button>
                            )}
                            {supplementFarmRestrictionsData.status === "I" && (
                              <button
                                className="edit"
                                type="button"
                                style={{ marginLeft: "60px" }}
                                onClick={handleEditFarmRestrictions}
                              >
                                {translation.buttons.edit}
                                <PencilIcon />
                              </button>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
                <SupplementInfoCard>
                  <NoteIcon /> {translation.supplement.form.information}
                </SupplementInfoCard>
              </div>
              {/* Footer */}
              <Footer>
                <Row type="flex" justify="space-between">
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="selectDiv"
                  >
                    {props.values.id === null ? (
                      <Select
                        value={saveOptions}
                        name="saveOptions"
                        onChange={(value) => setSaveOptions(value)}
                      >
                        <Option key="1" value="only_save">
                          {translation.supplement.saveOptions.only_save}
                        </Option>
                        <Option key="2 " value="save_and_create_new">
                          {
                            translation.supplement.saveOptions
                              .save_and_create_new
                          }
                        </Option>
                      </Select>
                    ) : null}
                  </Col>
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="buttonsDiv"
                  >
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={closeDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>

                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </Spin>
          </form>
        )}
      />
      <DrawerSupplementSustenanceCapacity
        drawerVisible={drawerSupplementSustenanceCapacityVisible}
        data={
          savedSustenanceCapacity
            ? savedSustenanceCapacity
            : sustenanceCapacityData
        }
        onSave={handleSaveSustenanceCapacity}
        onCancel={handleCancelSustenanceCapacity}
      />
      <DrawerSupplementRestrictions
        drawerVisible={drawerSupplementFarmRestrictionsVisible}
        data={
          savedFarmRestrictions
            ? savedFarmRestrictions
            : supplementFarmRestrictionsData
        }
        onSave={handleSaveFarmRestrictionsData}
        onCancel={handleCancelFarmRestrictionsData}
      />
      <DrawerSupplier />
    </Container>
  );
}
export default DrawerSupplement;
